import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import { templatesModel, type DocumentTemplate } from '@/entities/templates';

import type { TemplateVersionTarget } from '../config';
import { holdsTemplateContent } from '../lib';

import { useSaveTemplateVersion } from './useSaveTemplateVersion';

// The clinic's template list read again; a failed read keeps the list as it
// is.
export const useRefreshTemplateList = () => {
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );

  return async () => {
    if (!organizationId) {
      return;
    }
    await dispatch(
      templatesModel.fetchDocumentTemplates({
        organization_id: organizationId,
      }),
    )
      .unwrap()
      .catch(() => undefined);
  };
};

// The series' active version, the one a save is checked against, also while
// a newer draft exists. The template list is left as it is, so a page that
// shows it keeps the version it shows.
const useReadActiveTemplate = () => {
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector(
    organizationModel.selectors.selectRelevantOrganizationID,
  );

  return (source: DocumentTemplate) =>
    dispatch(
      templatesModel.fetchActiveDocumentTemplate({
        id: source.series_id,
        organizationId: source.organization_id || organizationId,
      }),
    ).unwrap();
};

// Every template change edited on the Templates page is saved here, as the
// next active version of the clinic's series, based on the version the edit
// started from.
export const useApplyTemplateVersion = () => {
  const saveVersion = useSaveTemplateVersion();
  const refreshList = useRefreshTemplateList();
  // The series' current version, read again after a save was refused because
  // another version had replaced the source; the page keeps the edited
  // version, and the refusal with it, on screen.
  const reload = useReadActiveTemplate();

  // A refusal because another version replaced the source is checked against
  // the series' current version: when that one holds these very changes, an
  // earlier save stored them though its answer was lost.
  const saveOrRecover = async ({ draft, source }: TemplateVersionTarget) => {
    try {
      return await saveVersion(draft, source);
    } catch (error) {
      if (
        !templatesModel.isSeriesMovedWriteError(error) ||
        templatesModel.isSeriesBusyWriteError(error)
      ) {
        throw error;
      }
      const current = await reload(source).catch(() => undefined);
      if (
        !current ||
        current.id === source.id ||
        !holdsTemplateContent(current, draft.template)
      ) {
        throw error;
      }
      return { template: current, warnings: [] };
    }
  };

  // The template list is read again for the saved version.
  const save = async (target: TemplateVersionTarget) => {
    const saved = await saveOrRecover(target);
    await refreshList();
    return saved;
  };

  return { save, reload };
};
